import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

const ContactModal = ({ isOpen, onClose, initialData = {}, onSubmitSuccess = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: initialData.category || 'general',
    subject: initialData.subject || '',
    message: initialData.message || '',
    context: initialData.context || {}
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Pre-fill user info if logged in
  useEffect(() => {
    const user = auth.currentUser;
    if (user && isOpen) {
      setFormData(prev => ({
        ...prev,
        name: user.displayName || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [isOpen]);

  // Update form when initialData changes
  useEffect(() => {
    if (initialData.category || initialData.subject || initialData.message || initialData.context) {
      setFormData(prev => ({
        ...prev,
        category: initialData.category || prev.category,
        subject: initialData.subject || prev.subject,
        message: initialData.message || prev.message,
        context: initialData.context || prev.context
      }));
    }
  }, [initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);

    try {
      if (!formData.name || !formData.email || !formData.message) {
        throw new Error('Please fill in all required fields');
      }

      const functions = getFunctions();
      const sendContactEmail = httpsCallable(functions, 'sendContactEmail');
      
      const payload = {
        name: formData.name,
        email: formData.email,
        category: formData.category,
        subject: formData.subject,
        message: formData.message,
        context: formData.context,
        userId: auth.currentUser?.uid || null,
        timestamp: new Date().toISOString()
      };

      const maxRetries = 2;
      let lastError;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          await sendContactEmail(payload);
          lastError = null;
          break;
        } catch (callErr) {
          lastError = callErr;
          if (attempt < maxRetries) {
            await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
          }
        }
      }
      if (lastError) throw lastError;

      setSuccess(true);
      
      if (onSubmitSuccess) {
        try {
          await onSubmitSuccess({
            message: formData.message,
            subject: formData.subject,
            category: formData.category,
            context: formData.context,
            name: formData.name,
            email: formData.email,
          });
        } catch (callbackErr) {
          console.error('Error in onSubmitSuccess callback:', callbackErr);
        }
      }
      
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (err) {
      console.error('Error sending contact form:', err);
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      category: 'general',
      subject: '',
      message: '',
      context: {}
    });
    setError('');
    setSuccess(false);
    setSending(false);
    onClose();
  };

  if (!isOpen) return null;

  const categories = [
    { value: 'general', label: 'General Support' },
    { value: 'feedback', label: 'Product Feedback' },
    { value: 'bug_report', label: 'Bug Report' },
    { value: 'report_listing', label: 'Report Listing' },
    { value: 'report_trade', label: 'Report Trade Issue' },
    { value: 'report_purchase', label: 'Report Purchase Issue' },
    { value: 'report_photo', label: 'Report Photo' },
    { value: 'report_message', label: 'Report Messages' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'account_issue', label: 'Account Issue' }
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', color: '#1f2937' }}>
            {formData.category === 'feedback' ? 'Share Feedback' : 'Contact Support'}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0 8px'
            }}
          >
            ×
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div style={{
            margin: '20px 24px',
            padding: '16px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            ✅ Message sent successfully! We'll get back to you soon.
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
            {/* Error Message */}
            {error && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {/* Name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Email */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {formData.category !== 'feedback' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Category *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  backgroundColor: 'white'
                }}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            )}

            {formData.category !== 'feedback' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Subject {formData.subject && '(auto-filled)'}
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Brief description of your issue"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            )}

            {/* Message */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Message *
              </label>
              <textarea
                required
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder={formData.category === 'feedback'
                  ? "What could we do better? Any features you'd love to see? We read every submission."
                  : "Please describe your issue or question in detail..."}
                rows={6}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleClose}
                disabled={sending}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: sending ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sending}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: sending ? '#9ca3af' : '#C89F3C',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: sending ? 'not-allowed' : 'pointer'
                }}
              >
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactModal;

